import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Star,
  ChefHat,
  Tag,
  Copy,
  Check,
  Truck,
  Gift,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Tilt3D,
  FloatingOrb,
  ParallaxImage,
} from "@/components/Tilt3D";
import { Marquee, Magnetic } from "@/components/Motion";

const CATEGORIES = [
  {
    name: "Ice Cream Scoops",
    img: "/images/ice-cream-scoops.png",
  },
  {
    name: "Premium Shakes",
    img: "/images/premium-shakes.png",
  },
  {
    name: "Waffles",
    img: "/images/waffles.png",
  },
  {
    name: "Sundaes",
    img: "/images/sundaes.png",
  },
  {
    name: "Falooda",
    img: "/images/falooda.png",
  },
  {
    name: "Add-Ons",
    img: "/images/add-ons.png",
  },
  {
    name: "Pizza",
    img: "/images/pizza.png",
  },
  {
    name: "Maggie",
    img: "/images/maggie.png",
  },
];

export default function Landing() {
  const [flavours, setFlavours] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [copiedCode, setCopiedCode] = useState("");

  useEffect(() => {
      api
        .get("/flavours")
        .then((r) => {
          const data = Array.isArray(r.data)
            ? r.data
            : Array.isArray(r.data?.flavours)
            ? r.data.flavours
            : [];

          setFlavours(data.slice(0, 8));
        })
        .catch((error) => {
          console.error("Failed to load flavours:", error);
          setFlavours([]);
        });

      api
        .get("/coupons")
        .then((r) => {
          const data = Array.isArray(r.data)
            ? r.data
            : Array.isArray(r.data?.coupons)
            ? r.data.coupons
            : [];

          const activeCoupons = data.filter(
            (coupon) => coupon.active !== false
          );

          setCoupons(activeCoupons);
        })
        .catch((error) => {
          console.error("Failed to load coupons:", error);
          setCoupons([]);
        });  
        }, []);

  const copyCoupon = async (code) => {
    try {
      await navigator.clipboard.writeText(code);

      setCopiedCode(code);

      setTimeout(() => {
        setCopiedCode("");
      }, 2000);
    } catch {
      const textarea = document.createElement("textarea");

      textarea.value = code;

      document.body.appendChild(textarea);

      textarea.select();

      document.execCommand("copy");

      document.body.removeChild(textarea);

      setCopiedCode(code);

      setTimeout(() => {
        setCopiedCode("");
      }, 2000);
    }
  };

  return (
    <div>

      {/* ================= HERO ================= */}

      <section className="relative overflow-hidden">

        <FloatingOrb
          className="top-10 -left-20"
          size={400}
          color="#FFB7B2"
          delay={0}
        />

        <FloatingOrb
          className="top-40 right-0"
          size={350}
          color="#A8E6CF"
          delay={2}
        />

        <FloatingOrb
          className="bottom-0 left-1/3"
          size={280}
          color="#B8E14A"
          delay={4}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 grid md:grid-cols-12 gap-8 items-center relative">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7"
          >

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-white/60 mb-6">

              <Sparkles className="w-4 h-4 text-[#B8E14A]" />

              <span className="text-xs uppercase tracking-widest text-[#E6F4F2]">
                Handcrafted since day one
              </span>

            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight font-medium mb-6">

              Welcome to
              <br />
              <span className="italic text-[#B8E14A]">
                Ice Cream
              </span>{" "}
              Paradise By{" "}
              <span className="text-[#FF69B4]">
                WOOP
              </span>
              !!
              

            </h1>

            <p className="text-lg md:text-xl text-[#E6F4F2] max-w-xl mb-10 leading-relaxed">

              100% natural ice cream, freshly handcrafted. Choose from 26
              premium flavours or design your own masterpiece in our Ice Cream
              Playground.

            </p>

            <div className="flex flex-wrap gap-4">

              <Magnetic>

                <Link
                  to="/menu"
                  data-testid="hero-explore-menu"
                  className="btn-primary"
                >
                  Explore Menu
                  <ArrowRight className="w-4 h-4" />
                </Link>

              </Magnetic>

              <Magnetic>

                <Link
                  to="/playground"
                  data-testid="hero-build-own"
                  className="btn-gold"
                >
                  Build Your Own
                </Link>

              </Magnetic>

              <Magnetic>

                <Link
                  to="/menu"
                  data-testid="hero-order-now"
                  className="btn-outline"
                >
                  Order Now
                </Link>

              </Magnetic>

            </div>

            <div className="flex items-center gap-6 mt-12 text-sm text-[#E6F4F2]">

              <div className="flex items-center gap-1">

                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#B8E14A] text-[#B8E14A]"
                  />
                ))}

                <span className="ml-2">
                  4.9 · 2,300+ happy customers
                </span>

              </div>

            </div>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="md:col-span-5 relative"
          >

            <div className="relative animate-float">

              <div className="absolute -inset-6 rounded-[3.5rem] bg-gradient-to-br from-[#B8E14A]/30 via-[#FFB7B2]/20 to-[#A8E6CF]/20 blur-2xl animate-spin-slow" />

              <ParallaxImage
                src="/images/hero-icecream.png"
                alt="Ice cream showcase"
                className="relative rounded-[3rem] w-full aspect-[4/5]"
              />

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 glass rounded-3xl px-6 py-4 flex items-center gap-3"
              >

                <ChefHat className="w-6 h-6 text-[#B8E14A]" />

                <div>

                  <div className="font-heading text-xl">
                    26 Flavours
                  </div>

                  <div className="text-xs text-[#E6F4F2] uppercase tracking-widest">
                    crafted daily
                  </div>

                </div>

              </motion.div>

              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="absolute -top-6 -right-6 glass rounded-full px-5 py-3 text-sm font-medium animate-wobble"
              >

                <span className="text-[#B8E14A]">
                  ₹80
                </span>{" "}
                starting

              </motion.div>

            </div>

          </motion.div>

        </div>

      </section>

      {/* ================= MARQUEE ================= */}

      <section className="relative border-y-2 border-[#B8E14A]/40 bg-[#1F5F5C] overflow-hidden">

        <Marquee
          items={[
            "Belgian Dark",
            "Lotus Biscoff",
            "Kesar Pista",
            "Tender Coconut",
            "Red Velvet",
            "Butterscotch",
            "Blueberry Cheese",
            "Roasted Almond",
            "Choco Brownie",
            "Sitaphal",
          ]}
        />

      </section>

      {/* ================= COUPONS ================= */}

      {coupons.length > 0 && (

        <section className="max-w-7xl mx-auto px-4 md:px-12 py-12 md:py-20">

          <div className="text-center mb-10">

            <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-[#B8E14A] mb-3">

              <Gift className="w-4 h-4" />

              Special Deals

            </div>

            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl">
              Coupons
            </h2>

            <p className="text-[#E6F4F2] mt-3 max-w-2xl mx-auto">
              Save more on your favourite treats with our latest coupons.
            </p>

          </div>

          {/* FREE DELIVERY */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 rounded-3xl p-5 md:p-6 bg-gradient-to-r from-[#1F5F5C] to-[#2A7A76] border border-[#B8E14A]/40 shadow-xl"
          >

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">

              <div className="flex items-center gap-4">

                <div className="w-12 h-12 rounded-2xl bg-[#B8E14A]/20 flex items-center justify-center">

                  <Truck className="w-6 h-6 text-[#B8E14A]" />

                </div>

                <div>

                  <div className="font-heading text-xl md:text-2xl text-white">
                    FREE DELIVERY
                  </div>

                  <div className="text-sm text-white/80">
                    On orders above ₹499
                  </div>

                </div>

              </div>

              <Link
                to="/menu"
                className="btn-gold"
              >
                Order Now
                <ArrowRight className="w-4 h-4" />
              </Link>

            </div>

          </motion.div>

          {/* COUPONS */}

          <div className="mb-10">

            <div className="flex items-center gap-2 mb-5">

              <Tag className="w-5 h-5 text-[#B8E14A]" />

              <h3 className="font-heading text-2xl">
                Available Coupons
              </h3>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">

              {coupons.map((coupon, i) => (

                <motion.div
                  key={coupon.id || coupon.code}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative overflow-hidden rounded-3xl bg-white text-[#1F5F5C] p-5 shadow-lg border border-white/50"
                >

                  <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-[#B8E14A]/20" />

                  <div className="relative">

                    <div className="flex items-center justify-between mb-4">

                      <div className="w-10 h-10 rounded-2xl bg-[#B8E14A]/20 flex items-center justify-center">

                        <Tag className="w-5 h-5 text-[#1F5F5C]" />

                      </div>

                      <span className="text-xs font-semibold uppercase tracking-wider bg-[#B8E14A] text-[#1F5F5C] px-3 py-1 rounded-full">

                        {coupon.discount_percent}% OFF

                      </span>

                    </div>

                    <div className="font-heading text-2xl mb-2">
                      {coupon.code}
                    </div>

                    <p className="text-sm text-gray-600 min-h-[42px]">
                      {coupon.description}
                    </p>

                    <div className="text-xs text-gray-500 mt-3">

                      {Number(coupon.min_order || 0) > 0
                        ? `Minimum order: ₹${Number(
                            coupon.min_order
                          ).toFixed(0)}`
                        : "No minimum order"}

                    </div>

                    <button
                      onClick={() => copyCoupon(coupon.code)}
                      className="w-full mt-5 px-4 py-2.5 rounded-full bg-[#1F5F5C] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#2A7A76] transition"
                    >

                      {copiedCode === coupon.code ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy Code
                        </>
                      )}

                    </button>

                  </div>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

      )}

      {/* ================= CATEGORIES ================= */}

      <section className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-16">

        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">

          <div>

            <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-2">
              Our Universe
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl">
              Explore by Category
            </h2>

          </div>

          <Link
            to="/menu"
            data-testid="see-full-menu"
            className="btn-outline"
          >
            See Full Menu
          </Link>

        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">

          {CATEGORIES.map((c, i) => (

            <motion.div
              key={c.name}
              initial={{
                opacity: 0,
                y: 30,
                rotateX: -20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
                rotateX: 0,
              }}
              viewport={{ once: true }}
              transition={{
                delay: i * 0.05,
                duration: 0.7,
              }}
              style={{ perspective: 1000 }}
            >

              <Tilt3D
                max={15}
                className="rounded-3xl"
              >

                <Link
                  to={`/menu?cat=${encodeURIComponent(c.name)}`}
                  data-testid={`category-${c.name
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                  className="block relative rounded-3xl overflow-hidden aspect-[3/4] group card-hover"
                >

                  <img
                    src={c.img}
                    alt={c.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/80 via-transparent to-transparent" />

                  <div className="absolute bottom-4 left-4 right-4">

                    <div className="font-heading text-xl md:text-2xl text-white">
                      {c.name}
                    </div>

                  </div>

                </Link>

              </Tilt3D>

            </motion.div>

          ))}

        </div>

      </section>

      {/* ================= POPULAR FLAVOURS ================= */}

      <section className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-16">

        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">

          <div>

            <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-2">
              Signature Flavours
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl">
              Freshly Crafted Every Morning
            </h2>

          </div>

        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">

          {flavours.map((f, i) => (

            <motion.div
              key={f.id}
              initial={{
                opacity: 0,
                y: 30,
                scale: 0.95,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              viewport={{ once: true }}
              transition={{
                delay: i * 0.05,
                duration: 0.6,
              }}
              className="w-full h-full"
            >

              <Tilt3D
                max={10}
                className="rounded-3xl w-full h-full"
              >

                <div className="glass rounded-3xl p-4 h-full flex flex-col">

                  <div className="aspect-square rounded-2xl overflow-hidden mb-4 relative w-full">

                    <img
                      src={f.image}
                      alt={f.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-white/30 to-transparent opacity-0 hover:opacity-100 transition" />

                  </div>

                  <div className="font-heading text-xl">
                    {f.name}
                  </div>

                  <div className="flex items-center justify-between mt-2 text-sm">

                    <span className="text-[#E6F4F2]">
                      {f.calories} cal
                    </span>

                    <span className="font-semibold text-[#B8E14A]">
                      ₹{f.price}
                    </span>

                  </div>

                </div>

              </Tilt3D>

            </motion.div>

          ))}

        </div>

        <div className="text-center mt-10">

          <Link
            to="/menu"
            data-testid="view-all-flavours"
            className="btn-primary"
          >
            View All 26 Flavours
            <ArrowRight className="w-4 h-4" />
          </Link>

        </div>

      </section>

      {/* ================= PLAYGROUND CTA ================= */}

      <section className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-16">

        <div className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] p-6 md:p-16 bg-gradient-to-br from-[#1F5F5C] via-[#2A7A76] to-[#1F5F5C] border border-[#B8E14A]/30">

          <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">

            <div>

              <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-3">
                Signature Experience
              </div>

              <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl leading-tight mb-6 text-white">

                The Ice Cream{" "}

                <span className="italic text-[#B8E14A]">
                  Playground
                </span>

              </h2>

              <p className="text-lg text-white/90 mb-8 leading-relaxed">

                Pick your cup, cone, up to 5 flavours and endless
                add-ons. Watch it come to life with live preview &
                pricing.

              </p>

              <Link
                to="/playground"
                data-testid="playground-cta"
                className="btn-gold"
              >
                Start Building
                <ArrowRight className="w-4 h-4" />
              </Link>

            </div>

            <div className="relative">

              <img
                src="/images/chocolate-icecream.png"
                alt="playground"
                className="rounded-3xl shadow-2xl w-full aspect-square object-cover"
              />

            </div>

          </div>

        </div>

      </section>

      {/* ================= FOUNDER ================= */}

      <FounderTeaser />

    </div>
  );
}

function FounderTeaser() {
  const [founder, setFounder] = useState(null);

  useEffect(() => {
    api
      .get("/founder")
      .then((r) => setFounder(r.data))
      .catch(() => {});
  }, []);

  if (!founder) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-12 py-16 md:py-24">

      <div className="glass rounded-[2rem] md:rounded-[3rem] p-6 md:p-16 grid md:grid-cols-2 gap-10 items-center">

        <div className="relative">

          <img
            src={founder.image || "/images/founder.png"}
            alt={`${founder.name}, Founder`}
            className="rounded-[2rem] w-full aspect-[4/5] object-cover shadow-xl object-top"
          />

          <div className="absolute -bottom-5 -right-5 bg-[#B8E14A] text-white px-5 py-3 rounded-full text-xs uppercase tracking-widest">
            {founder.name} · Founder
          </div>

        </div>

        <div>

          <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-3">
            Meet Our Founder
          </div>

          <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl leading-tight mb-6">

            Born from{" "}
            <span className="italic">
              passion
            </span>
            , served with love.

          </h2>

          <p className="text-lg text-[#E6F4F2] leading-relaxed mb-6">
            {founder.description1}
          </p>

          <div className="text-sm text-[#B7D8D5]">
            {founder.location}
          </div>

          <div className="mt-8">

            <Link
              to="/founder"
              data-testid="learn-more-founder"
              className="btn-outline"
            >
              Read the Full Story
            </Link>

          </div>

        </div>

      </div>

    </section>
  );
}